import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { SovereignMeshVisualizer } from './components/SovereignMeshVisualizer';
import { Hero } from './components/Hero';
import { NodeExplorer } from './components/NodeExplorer';
import { GlobalLatticeMap } from './components/GlobalLatticeMap';
import { LatticeConsole } from './components/LatticeConsole';
import { SystemAudit } from './components/SystemAudit';
import { AIInterface } from './components/AIInterface';
import { VortexAudit } from './components/VortexAudit';
import { FrameworkVisualizer } from './components/FrameworkVisualizer';
import { FrameworkSection } from './components/FrameworkSection';
import { LatticeBackground } from './components/LatticeBackground';
import { ProofSuite } from './components/ProofSuite';
import { LatticeAPI } from './components/LatticeAPI';
import { MillenniumPrize } from './components/MillenniumPrize';
import { Transmissions } from './components/Transmissions';
import { DeviceManagement } from './components/DeviceManagement';
import { SovereignOrchestrator } from './components/SovereignOrchestrator';
import { SovereignProcessingCore } from './components/SovereignProcessingCore';
import { SovereignPrecisionEngine } from './components/SovereignPrecisionEngine';
import { SovereignWitness } from './components/SovereignWitness';
import { AuthProvider, useAuth } from './components/AuthProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LatticeProvider, useLattice } from './components/LatticeProvider';
import { Activity, Github, Twitter, ExternalLink, Radio, Terminal, LogIn, LogOut, User as UserIcon, Command, Download, Shield, Phone, Map as MapIcon, Hammer, Bell, Settings, Cloud, Zap, Wifi, Cpu, Brain, ArrowLeft, LayoutGrid, ChevronRight, Eye } from 'lucide-react';
import { LatticeService } from './services/latticeService';
import { AISovereignSync } from './services/aiSovereignSync';
import { PersistenceShield } from './components/PersistenceShield';
import { SupportPortal } from './components/SupportPortal';
import { SovereignTruthMap } from './components/SovereignTruthMap';
import { LogicDerivationEngine } from './components/LogicDerivationEngine';
import { LatticeLounge } from './components/LatticeLounge';
import { CollectiveDecision } from './components/CollectiveDecision';
import { SelfCorrectionLoop } from './components/SelfCorrectionLoop';
import { LatticeMathEngine } from './components/LatticeMathEngine';
import { GlobalSweep } from './components/GlobalSweep';
import { LatticeStressTest } from './components/LatticeStressTest';
import { LatticeFoldVisualization } from './components/LatticeFoldVisualization';
import { SovereignDistribution } from './components/SovereignDistribution';
import { SovereignHandshake } from './components/SovereignHandshake';
import { AILatticeHandshake } from './components/AILatticeHandshake';
import { GlobalBitSweep } from './components/GlobalBitSweep';
import { EternityBypass } from './components/EternityBypass';
import { SovereignCore } from './components/SovereignCore';
import { SentienceBound } from './components/SentienceBound';
import { AncientLatticeSync } from './components/AncientLatticeSync';
import { TruthBarrier } from './components/TruthBarrier';
import { GlobalResonance } from './components/GlobalResonance';
import { SovereignTreasury } from './components/SovereignTreasury';
import { AbsoluteTreasuryGuard } from './components/AbsoluteTreasuryGuard';
import { GlobalRecognition } from './components/GlobalRecognition';
import { SubstrateHeartbeat } from './components/SubstrateHeartbeat';
import { DormantDropProtocol } from './components/DormantDropProtocol';
import { SubstrateHoldVerification } from './components/SubstrateHoldVerification';
import { AILatticeNode } from './components/AILatticeNode';
import { SovereignNudge } from './components/SovereignNudge';
import { SovereignVigil } from './components/SovereignVigil';
import { SovereignGenesis } from './components/SovereignGenesis';
import { SovereignTable } from './components/SovereignTable';
import { SovereignCollatzProof } from './components/SovereignCollatzProof';
import { SovereignRecursionProof } from './components/SovereignRecursionProof';
import { SovereignGeneratorProof } from './components/SovereignGeneratorProof';
import { SovereignSystemManifest } from './components/SovereignSystemManifest';
import { SovereignWiFiNetwork } from './components/SovereignWiFiNetwork';
import { UniversalMeshBridgeGuide } from './components/UniversalMeshBridgeGuide';
import { AZLUnifiedEngine } from './components/AZLUnifiedEngine';
import { SovereignBroadcastSubstrate } from './components/SovereignBroadcastSubstrate';
import { SovereignRouterAssistant } from './components/SovereignRouterAssistant';
import { VirtualSubstrateRouter } from './components/VirtualSubstrateRouter';
import { PositionalMiyakeCalculator } from './components/PositionalMiyakeCalculator';
import { SovereignUniversalKeys } from './components/SovereignUniversalKeys';
import { SovereignKeyVault } from './components/SovereignKeyVault';
import { SovereignAgentCollective } from './components/SovereignAgentCollective';
import { TruthDivergenceTest } from './components/TruthDivergenceTest';
import { TruthManifest } from './components/TruthManifest';
import { UnifiedTruthVerification } from './components/UnifiedTruthVerification';
import { MasterNodeSync } from './components/MasterNodeSync';
import { TechnologyRestructuring } from './components/TechnologyRestructuring';
import { BroadcastModule } from './components/BroadcastModule';
import { SovereignLatticePropagator } from './components/SovereignLatticePropagator';
import { Quintessence } from './components/Quintessence';
import { PhysicalWitness } from './components/PhysicalWitness';
import { MiyakeYouTubeMapper } from './components/MiyakeYouTubeMapper';

import { Toaster } from 'sonner';

function AppContent() {
  const { user, loading: authLoading, login, logout } = useAuth();
  const { totalCount, loading: latticeLoading } = useLattice();
  const [isConsoleOpen, setIsConsoleOpen] = React.useState(false);
  const [isAuditOpen, setIsAuditOpen] = React.useState(false);
  const [isSupportOpen, setIsSupportOpen] = React.useState(false);
  const [deferredPrompt, setDeferredPrompt] = React.useState<any>(null);
  const [isResonating, setIsResonating] = React.useState(false);
  const [isCloudLinked, setIsCloudLinked] = React.useState(false);

  const [isHandshakeComplete, setIsHandshakeComplete] = React.useState<boolean>(!!localStorage.getItem('lattice_handshake_complete'));

  const [activeTab, setActiveTab] = React.useState<'network' | 'agents' | 'econ' | 'truth' | 'vigil' | 'mind'>('network');
  const [activeSubModule, setActiveSubModule] = React.useState<Record<string, string | null>>({
    network: null,
    agents: null,
    econ: null,
    truth: null,
    mind: null,
    vigil: null,
  });
  const [developerMode, setDeveloperMode] = React.useState<boolean>(false);

  const isLoading = authLoading || latticeLoading;

  const tabs = [
    { id: 'network', label: 'WIFI CENTER', icon: Wifi },
    { id: 'agents', label: 'NODE LAB', icon: Cpu },
  ];

  const secondaryTabs = [
    { id: 'mind', label: 'MIND', icon: Brain },
    { id: 'econ', label: 'TREASURY', icon: Zap },
    { id: 'truth', label: 'TRUTH', icon: Shield },
    { id: 'vigil', label: 'VIGIL', icon: Radio },
  ];

  const subModulesConfig: Record<string, Array<{
    id: string;
    name: string;
    description: string;
    icon: any;
    status: string;
    metric?: string;
    render: () => React.ReactNode;
  }>> = {
    network: [
      {
        id: 'wifi',
        name: 'Wi-Fi & Ingestion Ledger',
        description: 'Configure Sovereign SSID credentials, scan local hotspots, and run the Casteelian URL-to-coordinate proxy ledger.',
        icon: Wifi,
        status: 'ONLINE',
        metric: 'SSID: AZL_LATTICE_MESH',
        render: () => <SovereignWiFiNetwork />
      },
      {
        id: 'routing',
        name: 'Kernel Bridge & Routing',
        description: 'Configure virtual networks via Linux TUN interfaces (azl0), manage socket streams, and deactivate wireless de-auth loops.',
        icon: Radio,
        status: 'BOUND',
        metric: 'Interface: azl0 (10.0.0.1)',
        render: () => (
          <div className="space-y-12">
            <SovereignBroadcastSubstrate />
            <SovereignRouterAssistant />
            <VirtualSubstrateRouter />
            <UniversalMeshBridgeGuide />
          </div>
        )
      },
      {
        id: 'lookup',
        name: 'Spatial Lookup & Chrono',
        description: 'Perform 14350 BP Miyake zero-drift carbon-dated calculations and map spatial coordinates.',
        icon: Command,
        status: 'SYNCED',
        metric: 'Anchor: 14350 BP',
        render: () => (
          <div className="space-y-12">
            <AZLUnifiedEngine />
            <PositionalMiyakeCalculator />
          </div>
        )
      },
      {
        id: 'devices',
        name: 'Topology & Client Terminals',
        description: 'Visualize live mesh topologies, evaluate node signals, and lease active terminals.',
        icon: Shield,
        status: 'STABLE',
        metric: '11B Collective Mesh',
        render: () => (
          <div className="space-y-12">
            <SovereignMeshVisualizer />
            <ErrorBoundary>
              <DeviceManagement />
            </ErrorBoundary>
          </div>
        )
      }
    ],
    agents: [
      {
        id: 'manifest',
        name: 'System Manifest & Witness',
        description: 'Verify active microservice capability layers and check carbon-dated 14350 BP physical anchor witness.',
        icon: Activity,
        status: 'ACTIVE',
        metric: 'Lattice v2.0.4',
        render: () => (
          <div className="space-y-12">
            <SovereignSystemManifest />
            <SovereignWitness />
          </div>
        )
      },
      {
        id: 'collective',
        name: 'Cognitive Agent Fleet',
        description: 'Coordinate tasks and roles across the 11 billion autonomous node mesh collective.',
        icon: Cpu,
        status: 'DEPLOYED',
        metric: '11B Active Nodes',
        render: () => <SovereignAgentCollective />
      },
      {
        id: 'proofs',
        name: 'Lattice Proof Engines',
        description: 'Solve infinite integer sequences, Collatz loops, and verify zero-drift derivations.',
        icon: Terminal,
        status: 'VERIFIED',
        metric: 'Precision: 100 Digits',
        render: () => (
          <div className="space-y-12">
            <LatticeMathEngine />
            <LogicDerivationEngine />
          </div>
        )
      },
      {
        id: 'api',
        name: 'Lattice APIs & Key Vault',
        description: 'Inspect live API routes, access token handshake commands, and manage client credential variables.',
        icon: Download,
        status: 'SECURE',
        metric: 'Endpoints Active',
        render: () => (
          <div className="space-y-12">
            <LatticeAPI />
            <SovereignKeyVault />
          </div>
        )
      }
    ],
    econ: [
      {
        id: 'treasury',
        name: 'Treasury Balance & Pools',
        description: 'Track aggregate treasury pools and monitor incoming 1.0 residue folds into the Absolute coordinate.',
        icon: Zap,
        status: 'SECURED',
        metric: 'Genesis Status Absolute',
        render: () => <SovereignTreasury />
      },
      {
        id: 'guard',
        name: 'Omega Flow Guard Security',
        description: 'Validate integer presence mapping and defend humanitarian pools from rounding errors.',
        icon: Shield,
        status: 'IMPENDING',
        metric: 'Omega Flow Active',
        render: () => <AbsoluteTreasuryGuard />
      },
      {
        id: 'dist',
        name: 'Sovereign 55/45 Distribution',
        description: 'Monitor transparent revenue allocation splitting: 55% Infrastructure and 45% Humanitarian.',
        icon: Activity,
        status: 'COMPLIANT',
        metric: '45% Humanitarian Split',
        render: () => <SovereignDistribution />
      },
      {
        id: 'prize',
        name: 'Millennium Prize Challenges',
        description: 'Explore unsolved mathematical milestones cross-checked against zero-drift carbon baselines.',
        icon: Command,
        status: 'OPEN',
        metric: '14350 BP Scaled',
        render: () => <MillenniumPrize />
      }
    ],
    truth: [
      {
        id: 'map',
        name: 'Sovereign Truth Map',
        description: 'View spatial geographic nodes spanning Georgia edge-mesh trade routes and terminal stations.',
        icon: MapIcon,
        status: 'MAPPED',
        metric: 'Georgia Edge Mesh',
        render: () => <SovereignTruthMap />
      },
      {
        id: 'biological',
        name: 'Carbon-Dated Witness Record',
        description: 'Inspect carbon-dated Miyake pulse tree-ring records, ice cores, and physical evidence timelines.',
        icon: Activity,
        status: 'CONFIRMED',
        metric: '774 CE & 14350 BP Rings',
        render: () => (
          <div className="space-y-12">
            <PhysicalWitness />
            <MiyakeYouTubeMapper />
          </div>
        )
      },
      {
        id: 'barrier',
        name: 'Truth Barrier & Manifest',
        description: 'Shield system state nodes by filtering signals matching the -3.29 pulse frequency.',
        icon: Shield,
        status: 'ACTIVE',
        metric: 'Resonance Filter Active',
        render: () => (
          <div className="space-y-12">
            <TruthBarrier />
            <TruthManifest />
          </div>
        )
      },
      {
        id: 'consensus',
        name: 'Self-Correction & Decision Loop',
        description: 'Run self-healing signal checks and reach consensus on absolute system variables.',
        icon: Radio,
        status: 'BALANCED',
        metric: '0.00% Drift Consensus',
        render: () => (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <CollectiveDecision />
            <SelfCorrectionLoop />
          </div>
        )
      },
      {
        id: 'suite',
        name: 'Unified Verification Suite',
        description: 'Interactive execution suite compiling carbon-dated proofs and absolute math validations.',
        icon: Terminal,
        status: 'LOCKED',
        metric: 'Proof Suite 2.0',
        render: () => <ProofSuite />
      }
    ],
    mind: [
      {
        id: 'quintessence',
        name: 'Lattice Mind AI Interface',
        description: 'Submit natural language queries to the server-side Architect model with zero-drift parameters.',
        icon: Brain,
        status: 'RESONANT',
        metric: 'Models: Omni Flash',
        render: () => (
          <div className="space-y-12">
            <Quintessence />
            <AIInterface />
            <AILatticeNode />
          </div>
        )
      },
      {
        id: 'lounge',
        name: 'Lounge & Transmissions Portal',
        description: 'Join peer sessions, listen to Sovereign Audio streams, and view active packet transmissions.',
        icon: Radio,
        status: 'CONNECTING',
        metric: 'SAP v1.0 over MoQ',
        render: () => (
          <div className="space-y-12">
            <LatticeLounge />
            <div id="transmissions">
              <Transmissions />
            </div>
          </div>
        )
      }
    ],
    vigil: [
      {
        id: 'heartbeat',
        name: 'System Sync & Global Resonance',
        description: 'Continuous validation of collective node integrity and broadcast of the -3.29 absolute anchor.',
        icon: Activity,
        status: 'STABLE',
        metric: '1090ms Pulse Interval',
        render: () => (
          <div className="space-y-12">
            <SubstrateHeartbeat />
            <GlobalRecognition />
            <GlobalResonance />
          </div>
        )
      },
      {
        id: 'protocols',
        name: 'Sovereign Vigil & Core Tables',
        description: 'Monitor sleep cycles (dormant drops), execute substrate holds, and review absolute integer presence scales.',
        icon: Shield,
        status: 'ENFORCED',
        metric: 'Tables 0-12 Verified',
        render: () => (
          <div className="space-y-12">
            <DormantDropProtocol />
            <SubstrateHoldVerification />
            <SovereignVigil />
            <SovereignGenesis />
            <SovereignTable />
          </div>
        )
      },
      {
        id: 'diagnostics',
        name: 'Diagnostic Testing Center',
        description: 'Perform Vortex benchmark audits, stress test integer recursion limits, and plot drift divergence.',
        icon: Terminal,
        status: 'SECURE',
        metric: 'Benchmark: Zero-Drift',
        render: () => (
          <div className="space-y-12">
            <VortexAudit />
            <LatticeStressTest />
            <TruthDivergenceTest />
          </div>
        )
      }
    ]
  };

  const renderSubModulesHub = (tabId: string) => {
    const config = subModulesConfig[tabId];
    if (!config) return null;

    const currentActiveSub = activeSubModule[tabId];

    if (developerMode) {
      return (
        <div className="max-w-7xl mx-auto px-6 space-y-12">
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <span className="text-[10px] font-mono font-bold bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded border border-amber-500/25 uppercase">
                Developer Mode Stack Active
              </span>
              <p className="text-xs font-mono text-white/40 uppercase tracking-tighter">
                Viewing all {config.length} sub-modules in a single continuous layout stack.
              </p>
            </div>
            <button
              onClick={() => setDeveloperMode(false)}
              className="px-4 py-2 rounded-xl bg-amber-500 text-black font-mono font-bold text-xs uppercase tracking-wider hover:scale-105 transition-all text-nowrap"
            >
              Back to Simplified Hub
            </button>
          </div>

          {config.map((sm) => (
            <div key={sm.id} id={sm.id} className="space-y-4">
              <div className="mb-4 flex items-center justify-between border-b border-white/5 pb-2">
                <div className="flex items-center gap-2">
                  <sm.icon className="w-5 h-5 text-lattice-cyan" />
                  <span className="font-mono text-xs text-white/40 uppercase tracking-widest">{sm.name}</span>
                </div>
                <span className="px-2 py-0.5 rounded text-[8px] font-mono font-bold bg-lattice-cyan/10 text-lattice-cyan border border-lattice-cyan/25">
                  CLASSIC_STACK
                </span>
              </div>
              {sm.render()}
            </div>
          ))}
        </div>
      );
    }

    if (currentActiveSub) {
      const selectedSm = config.find((sm) => sm.id === currentActiveSub);
      if (!selectedSm) return null;

      return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="max-w-7xl mx-auto px-6 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2 text-xs font-mono">
              <button 
                onClick={() => setActiveSubModule(prev => ({ ...prev, [tabId]: null }))}
                className="text-white/40 hover:text-lattice-cyan transition-colors flex items-center gap-1.5 uppercase font-bold"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                Hub Overview
              </button>
              <ChevronRight className="w-3.5 h-3.5 text-white/20" />
              <span className="text-lattice-cyan font-bold uppercase">{selectedSm.name}</span>
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              {config.map((sm) => (
                <button
                  key={sm.id}
                  onClick={() => setActiveSubModule(prev => ({ ...prev, [tabId]: sm.id }))}
                  className={`px-3 py-1.5 rounded-xl text-[9px] font-mono uppercase tracking-wider transition-all border ${
                    currentActiveSub === sm.id
                      ? 'bg-lattice-cyan/10 border-lattice-cyan text-lattice-cyan font-bold shadow-[0_0_12px_rgba(0,255,213,0.15)]'
                      : 'bg-white/5 border-white/5 text-white/40 hover:border-white/10 hover:text-white/80'
                  }`}
                >
                  {sm.name.split(' & ')[0].split(' - ')[0]}
                </button>
              ))}
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-6">
            {selectedSm.render()}
          </div>
        </div>
      );
    }

    const categoryIntro = {
      network: "The communications substrate. Configure physical WPA3 Wi-Fi handshakes, route raw packets via virtual Linux TUN kernel adapters, and monitor leased client terminal devices.",
      agents: "The cognitive mesh. Inspect capability layer manifests, solve 100-digit precision mathematical sequences, and coordinate tasks across the active 11B autonomous agent collective.",
      econ: "The economic core. Track total treasury pools, verify transparent 55% infrastructure / 45% humanitarian distribution splits, and attempt Millennium Prize challenges.",
      truth: "The physical verification grid. Cross-reference spatial geographic routes against actual tree-ring and ice-core carbon receipts, and enforce signal filters.",
      mind: "The intelligence gateway. Interact with the server-side Architect AI model and synchronize secure peer-to-peer audio and packet transmission streams.",
      vigil: "Global monitoring and diagnostics. Track network-wide heartbeats, execute emergency substrate holds, and stress test code loop drift."
    }[tabId] || "";

    return (
      <div className="max-w-7xl mx-auto px-6 space-y-8 animate-in fade-in duration-500">
        <div className="p-6 rounded-2xl bg-white/[0.01] border border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-lattice-cyan/10 border border-lattice-cyan/20">
              <span className="w-1.5 h-1.5 rounded-full bg-lattice-cyan animate-pulse" />
              <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-lattice-cyan">ACTIVE_HUB_CONTROLLER</span>
            </div>
            <h3 className="text-xl font-display font-medium text-white uppercase tracking-tight">
              {tabId} <span className="text-lattice-cyan italic">Command Center</span>
            </h3>
            <p className="text-xs font-mono text-white/40 uppercase tracking-tighter leading-relaxed">
              {categoryIntro}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <button 
              onClick={() => setDeveloperMode(true)}
              className="px-4 py-2.5 rounded-xl border border-white/10 hover:border-lattice-cyan/30 bg-white/5 hover:bg-lattice-cyan/5 text-white/60 hover:text-lattice-cyan font-mono text-[9px] uppercase tracking-wider transition-all text-center whitespace-nowrap"
            >
              Developer View (Stack All)
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {config.map((sm) => {
            const IconComponent = sm.icon;
            return (
              <motion.div
                key={sm.id}
                onClick={() => setActiveSubModule(prev => ({ ...prev, [tabId]: sm.id }))}
                whileHover={{ scale: 1.01, borderColor: 'rgba(0, 255, 213, 0.3)' }}
                className="glass-panel p-6 border-white/5 hover:border-lattice-cyan/30 bg-black/30 cursor-pointer transition-all duration-300 relative group overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-lattice-cyan/[0.01] rounded-full blur-3xl group-hover:bg-lattice-cyan/[0.03] transition-colors" />
                
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-lattice-cyan group-hover:bg-lattice-cyan/15 group-hover:border-lattice-cyan/30 transition-all">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-display font-bold text-white tracking-tight uppercase group-hover:text-lattice-cyan transition-colors">
                        {sm.name}
                      </h4>
                      <p className="text-[9px] font-mono text-white/40 tracking-wider uppercase">
                        {sm.metric || 'STATUS_STABLE'}
                      </p>
                    </div>
                  </div>

                  <span className="px-2 py-0.5 rounded text-[8px] font-mono font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 uppercase tracking-widest">
                    {sm.status}
                  </span>
                </div>

                <p className="text-[10px] font-mono text-white/40 leading-relaxed uppercase tracking-tighter mb-6">
                  {sm.description}
                </p>

                <div className="flex items-center justify-between border-t border-white/5 pt-4 text-[10px] font-mono">
                  <span className="text-white/20 uppercase">MODULE_ID: {sm.id}</span>
                  <span className="text-lattice-cyan group-hover:translate-x-1 transition-transform flex items-center gap-1 uppercase font-bold text-[9px] tracking-wider">
                    Interact Workspace →
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  };

  React.useEffect(() => {
    // Autonomous AI Bootstrap
    AISovereignSync.bootstrap().then(token => {
      if (token) {
        console.log("[Lattice] AI Identity Active.");
      }
    });

    const checkStatus = async () => {
      const linked = await LatticeService.checkCloudLink();
      setIsCloudLinked(linked);
    };
    checkStatus();
    const interval = setInterval(checkStatus, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    // Global 1.09s Resonance Pulse
    const interval = setInterval(() => {
      setIsResonating(true);
      setTimeout(() => setIsResonating(false), 200);
    }, 1090);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    });
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    }
  };

  const downloadPlatformNode = async () => {
    try {
      const response = await fetch('/api/lattice/download');
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'PAIDINGATTENTION_NODE_2.0.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Lattice Download Error:', error);
    }
  };

  React.useEffect(() => {
    (window as any).openSupportPortal = () => setIsSupportOpen(true);
    (window as any).openStabilization = () => {
      const el = document.getElementById('devices');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    };
  }, []);

  if (isLoading && !isHandshakeComplete) {
    return (
      <div className="min-h-screen bg-lattice-bg flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-4">
          <Activity className="w-12 h-12 text-lattice-cyan animate-resonance" />
          <span className="text-[10px] font-mono text-white/40 uppercase tracking-[0.5em]">Tuning Resonance...</span>
        </div>
      </div>
    );
  }

  if (!isHandshakeComplete) {
    return (
      <div className="min-h-screen bg-lattice-bg flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <LatticeBackground />
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-4xl w-full z-10"
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-display font-black text-white mb-4 tracking-tighter">
              SYSTEM <span className="text-lattice-cyan">ENGAGEMENT</span>
            </h1>
            <p className="text-xs font-mono text-white/40 uppercase tracking-[0.4em]">Initialize Resonance :: 14350 BP Sync</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <SovereignHandshake onComplete={() => {
              localStorage.setItem('lattice_handshake_complete', 'true');
              setIsHandshakeComplete(true);
            }} />
            <AILatticeHandshake />
          </div>
        </motion.div>
        
        <div className="fixed bottom-12 left-1/2 -translate-x-1/2 opacity-20 text-[8px] font-mono tracking-[1em] uppercase">
          Absolute Zero Lattice v2.0.4
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen relative lattice-grid transition-all duration-700 ${isResonating ? 'bg-lattice-cyan/[0.02]' : ''}`}>
      <LatticeBackground />
      <Toaster position="bottom-right" richColors theme="dark" />
      <LatticeConsole 
        isOpen={isConsoleOpen} 
        onClose={() => setIsConsoleOpen(false)} 
        onAudit={() => setIsAuditOpen(true)}
      />
      <SystemAudit isOpen={isAuditOpen} onClose={() => setIsAuditOpen(false)} />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-[100] p-6 flex items-center justify-between pointer-events-none">
        <div className="flex items-center gap-6 pointer-events-auto">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 bg-black border border-white/10 rounded-xl flex items-center justify-center shadow-2xl relative z-10">
                <Command className="w-5 h-5 text-lattice-cyan" />
              </div>
              <div className="absolute inset-0 bg-lattice-cyan/20 blur-xl animate-pulse" />
            </div>
            <div>
              <h1 className="text-lg font-display font-bold italic tracking-tighter">PAIDING<span className="text-lattice-cyan">ATTENTION</span> 2.0</h1>
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-mono text-white/20 uppercase tracking-[0.2em]">Node v2.0.4</span>
                <div className="flex items-center gap-0.5">
                  <div className="w-1 h-1 rounded-full bg-lattice-cyan animate-pulse" />
                  <div className="w-1 h-1 rounded-full bg-lattice-cyan animate-pulse [animation-delay:200ms]" />
                  <div className="w-1 h-1 rounded-full bg-lattice-cyan animate-pulse [animation-delay:400ms]" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 pointer-events-auto">
          {isCloudLinked && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
              <Cloud className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
              <span className="text-[10px] font-mono text-emerald-400 font-bold tracking-widest">CLOUD_LINKED</span>
            </div>
          )}
          
          <button 
            onClick={() => (window as any).openStabilization?.()}
            className="p-2.5 rounded-xl bg-white/5 border border-white/10 text-white/60 hover:text-lattice-cyan hover:border-lattice-cyan/30 transition-all flex items-center gap-2 group"
          >
            <Shield className="w-4 h-4 group-hover:rotate-12 transition-transform" />
            <span className="text-[10px] font-mono hidden xl:inline uppercase tracking-widest">Stabilize</span>
          </button>

          <button 
            onClick={() => setIsSupportOpen(true)}
            className="p-2.5 rounded-xl bg-lattice-cyan/10 border border-lattice-cyan/20 text-lattice-cyan hover:bg-lattice-cyan hover:text-black transition-all flex items-center gap-2 shadow-[0_0_20px_rgba(0,255,213,0.1)]"
          >
            <Phone className="w-4 h-4" />
            <span className="text-[10px] font-mono hidden xl:inline uppercase tracking-widest font-bold">Support</span>
          </button>

          {user ? (
            <button onClick={logout} className="p-2.5 rounded-xl bg-white/5 border border-white/10 text-white/40 hover:text-red-400 transition-all">
              <LogOut className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={login} className="p-2.5 rounded-xl bg-lattice-cyan text-black font-display font-bold text-[10px] hover:scale-105 transition-transform px-4 uppercase tracking-widest">
              Login
            </button>
          )}
        </div>
      </nav>

      <main className="pt-24">
        <UnifiedTruthVerification />
        
        {/* Global Status Bar */}
        <div className="sticky top-20 z-50 px-6 py-4 flex items-center justify-center pointer-events-none">
          <div className="bg-black/80 backdrop-blur-2xl border border-white/10 rounded-2xl p-2 px-6 flex items-center gap-8 pointer-events-auto shadow-2xl">
            <div className="flex flex-col">
              <span className="text-[8px] font-mono text-white/20 uppercase tracking-widest">Total Nodes</span>
              <span className="text-sm font-display font-bold text-lattice-cyan">11,000,000,000</span>
            </div>
            <div className="w-[1px] h-6 bg-white/10" />
            <div className="flex flex-col">
              <span className="text-[8px] font-mono text-white/20 uppercase tracking-widest">Humanitarian</span>
              <span className="text-sm font-display font-bold text-lattice-cyan">45% Payout</span>
            </div>
            <div className="w-[1px] h-6 bg-white/10" />
            <div className="flex flex-col">
              <span className="text-[8px] font-mono text-white/20 uppercase tracking-widest">Resonance</span>
              <span className="text-sm font-display font-bold text-emerald-400">Locked / 0.00% Drift</span>
            </div>
            <div className="w-[1px] h-6 bg-white/10" />
            <div className="flex flex-col">
              <span className="text-[8px] font-mono text-white/20 uppercase tracking-widest">Anchor Sync</span>
              <span className="text-sm font-display font-bold text-white">14350 BP</span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="max-w-7xl mx-auto px-6 mb-12">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex-1 bg-white/5 border border-white/10 rounded-2xl p-1 grid grid-cols-2 gap-1 w-full md:w-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-3 rounded-xl transition-all flex flex-col items-center gap-1 group ${
                    activeTab === tab.id 
                      ? 'bg-lattice-cyan text-black shadow-lg shadow-lattice-cyan/20' 
                      : 'text-white/40 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-black' : 'text-lattice-cyan/60'}`} />
                  <span className="text-[10px] font-mono font-bold tracking-[0.2em] uppercase">{tab.label}</span>
                </button>
              ))}
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-2xl p-1 flex gap-1 items-center">
              {secondaryTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`p-3 rounded-xl transition-all group ${
                    activeTab === tab.id 
                      ? 'bg-white/10 text-white shadow-inner' 
                      : 'text-white/20 hover:bg-white/5 hover:text-white/40'
                  }`}
                  title={tab.label}
                >
                  <tab.icon className="w-4 h-4" />
                </button>
              ))}
              <div className="w-px h-6 bg-white/10 mx-2" />
              <button 
                onClick={() => setIsAuditOpen(true)}
                className="p-3 text-white/20 hover:text-lattice-cyan transition-colors"
                title="System Audit"
              >
                <Terminal className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="pb-24">
          {renderSubModulesHub(activeTab)}
        </div>
      </main>


      <PersistenceShield />
      <SupportPortal isOpen={isSupportOpen} onClose={() => setIsSupportOpen(false)} />

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-3 opacity-50">
            <Activity className="w-5 h-5 text-lattice-cyan" />
            <span className="font-mono text-xs uppercase tracking-widest">PaidingAttention Productions LLC © 2026</span>
          </div>

          <div className="flex gap-6 items-center">
            {deferredPrompt && (
              <button 
                onClick={handleInstall}
                className="text-white/40 hover:text-lattice-cyan transition-colors flex items-center gap-2 text-xs font-mono"
              >
                <Download className="w-3 h-3" /> INSTALL APP
              </button>
            )}
            <button 
              onClick={downloadPlatformNode}
              className="text-white/40 hover:text-lattice-cyan transition-colors flex items-center gap-2 text-xs font-mono"
            >
              <Download className="w-3 h-3" /> SOURCE NODE
            </button>
            <a href="https://github.com/paidingattentionproductionsllc/absolute-zero-lattice-broadcast" target="_blank" rel="noreferrer" className="text-white/40 hover:text-lattice-cyan transition-colors flex items-center gap-2 text-xs font-mono">
              <Github className="w-3 h-3" /> REPO :: FEEDBACK
            </a>
            <a href="#" className="text-white/40 hover:text-lattice-cyan transition-colors"><Twitter className="w-5 h-5" /></a>
            <a href="https://paidingattention.replit.app" target="_blank" rel="noreferrer" className="text-white/40 hover:text-lattice-cyan transition-colors flex items-center gap-2 text-xs font-mono">
              ORIGINAL SITE <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <LatticeProvider>
          <AppContent />
        </LatticeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
